import { useNavigate } from "react-router-dom";
import { useTenantStats, useTenants } from "@/hooks/useTenants";
import type { Tenant, TenantStats } from "@/types";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Icon } from "@/components/ui/icons";
import {
  StatusBadge,
  DeployBadge,
  TrialBadge,
} from "@/components/ui/status-badges";
import { Donut, BarTrend, Sparkline } from "@/components/ui/charts";
import { PageHead, Btn } from "@/components/ui/page-components";

/* ---------- Stat Tile ---------- */
function StatTile({
  label,
  value,
  icon,
  tone,
  trend,
  spark,
  onClick,
}: {
  label: string;
  value: number;
  icon: string;
  tone: string;
  trend?: string;
  spark?: number[];
  onClick?: () => void;
}) {
  const tones: Record<string, string> = {
    blue: "text-blue2 bg-blue-50",
    green: "text-buttonColor bg-green-50",
    orange: "text-orange2 bg-orange-50",
    red: "text-red2 bg-red-50",
    teal: "text-greentab bg-teal-50",
    slate: "text-gray3 bg-slate-100",
  };
  return (
    <Card
      className={`p-4 transition-shadow hover:shadow-sm ${onClick ? "cursor-pointer" : ""}`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div
          className={`flex h-9 w-9 items-center justify-center rounded-lg ${tones[tone]}`}
        >
          <Icon name={icon} size={18} />
        </div>
        {trend && (
          <span className="flex items-center gap-0.5 text-[11px] font-semibold text-buttonColor">
            <Icon name="trending" size={12} />
            {trend}
          </span>
        )}
        {spark && <Sparkline values={spark} width={64} height={26} />}
      </div>
      <div className="mt-3 text-[26px] font-bold leading-none text-foreground">
        {value}
      </div>
      <div className="mt-1 text-[12px] font-medium text-muted-foreground">
        {label}
      </div>
    </Card>
  );
}

/* ---------- Status Breakdown Donut ---------- */
function StatusBreakdown({ stats }: { stats: Partial<TenantStats> }) {
  const segs = [
    { label: "Active", value: stats.active ?? 0, color: "#06832B" },
    { label: "Provisioning", value: stats.provisioning ?? 0, color: "#F37735" },
    { label: "Suspended", value: stats.suspended ?? 0, color: "#D31D35" },
  ].filter((s) => s.value > 0);
  return (
    <Card className="p-4">
      <h3 className="text-[13px] font-bold text-foreground">
        Status breakdown
      </h3>
      <div className="mt-3 flex items-center gap-5">
        <Donut
          segments={segs}
          centerLabel={stats.total ?? 0}
          centerSub="tenants"
          size={120}
          thickness={13}
        />
        <div className="flex-1 space-y-2">
          {segs.map((s) => (
            <div key={s.label} className="flex items-center gap-2 text-[12px]">
              <span
                className="h-2.5 w-2.5 rounded-sm"
                style={{ background: s.color }}
              />
              <span className="flex-1 text-muted-foreground">{s.label}</span>
              <span className="font-bold text-foreground">{s.value}</span>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

/* ---------- Deploy Trend ---------- */
const MOCK_TREND = [
  { week: "May 5", deploys: 3 },
  { week: "May 12", deploys: 5 },
  { week: "May 19", deploys: 2 },
  { week: "May 26", deploys: 7 },
  { week: "Jun 2", deploys: 4 },
  { week: "Jun 9", deploys: 6 },
];

function DeployTrendCard() {
  const total = MOCK_TREND.reduce((s, d) => s + d.deploys, 0);
  return (
    <Card className="p-4">
      <div className="flex items-baseline justify-between">
        <h3 className="text-[13px] font-bold text-foreground">Deployments</h3>
        <span className="text-[11px] text-muted-foreground">last 6 weeks</span>
      </div>
      <div className="mt-1 text-2xl font-bold text-foreground">
        {total}
        <span className="ml-1.5 text-[12px] font-medium text-muted-foreground">
          total
        </span>
      </div>
      <div className="mt-3">
        <BarTrend data={MOCK_TREND} height={70} />
      </div>
    </Card>
  );
}

/* ---------- Recent Tenants ---------- */
function RecentTenantsCard({
  tenants,
  navigate,
}: {
  tenants: Tenant[];
  navigate: (path: string) => void;
}) {
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h3 className="text-[13px] font-bold text-foreground">
          Recently updated
        </h3>
        <button
          onClick={() => navigate("/tenants")}
          className="flex items-center gap-1 text-[11px] font-semibold text-primary hover:underline"
        >
          View all <Icon name="arrowRight" size={12} />
        </button>
      </div>
      <div className="divide-y divide-border/60">
        {tenants.length === 0 && (
          <div className="px-4 py-8 text-center text-[13px] text-muted-foreground">
            No tenants yet.
          </div>
        )}
        {tenants.slice(0, 6).map((t) => (
          <button
            key={t.id}
            onClick={() => navigate(`/tenants/${t.id}`)}
            className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-muted/60"
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
              <Icon name="building" size={16} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13px] font-semibold text-foreground">
                {t.name}
              </span>
              <span className="block truncate text-[11px] text-muted-foreground">
                {t.domain || t.slug}
              </span>
            </span>
            <StatusBadge status={t.status} />
            <DeployBadge status={t.deployment_status} />
            <TrialBadge tenant={t} />
          </button>
        ))}
      </div>
    </Card>
  );
}

/* ---------- Trials Ending Soon ---------- */
function TrialsEndingCard({
  tenants,
  navigate,
}: {
  tenants: Tenant[];
  navigate: (path: string) => void;
}) {
  const soon = tenants
    .filter((t) => t.trial_ends_at)
    .map((t) => {
      const end = new Date(t.trial_ends_at + "T23:59:59");
      const days = Math.ceil((end.getTime() - Date.now()) / 86400000);
      return { t, days };
    })
    .filter((x) => x.days > 0 && x.days <= 7)
    .sort((a, b) => a.days - b.days);
  if (!soon.length) return null;
  return (
    <Card className="overflow-hidden border-orange-200">
      <div className="flex items-center gap-2 border-b border-orange-200 bg-orange-50 px-4 py-2.5">
        <Icon name="clock" size={16} className="text-orange2" />
        <h3 className="text-[13px] font-bold text-orange1">
          Trials ending soon
        </h3>
        <span className="ml-auto rounded-full bg-orange2 px-1.5 text-[10px] font-bold text-white">
          {soon.length}
        </span>
      </div>
      <div className="divide-y divide-border/60">
        {soon.map(({ t }) => (
          <button
            key={t.id}
            onClick={() => navigate(`/tenants/${t.id}`)}
            className="flex w-full items-center gap-3 px-4 py-2 text-left hover:bg-muted/60"
          >
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
              <Icon name="building" size={14} />
            </span>
            <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-foreground">
              {t.name}
            </span>
            <TrialBadge tenant={t} />
          </button>
        ))}
      </div>
    </Card>
  );
}

/* ---------- Main Page ---------- */
export default function DashboardPage() {
  const { data: stats, isLoading: statsLoading } = useTenantStats();
  const { data: tenants = [], isLoading: tenantsLoading } = useTenants();
  const navigate = useNavigate();

  const tiles = [
    {
      label: "Total tenants",
      value: stats?.total ?? 0,
      icon: "building",
      tone: "blue",
      spark: [6, 7, 7, 8, 9, 10],
    },
    {
      label: "Active",
      value: stats?.active ?? 0,
      icon: "checkCircle",
      tone: "green",
      trend: "+2",
    },
    {
      label: "Provisioning",
      value: stats?.provisioning ?? 0,
      icon: "clock",
      tone: "orange",
    },
    {
      label: "Suspended",
      value: stats?.suspended ?? 0,
      icon: "alert",
      tone: "red",
    },
    {
      label: "Deployed",
      value: stats?.deployed ?? 0,
      icon: "rocket",
      tone: "teal",
    },
    {
      label: "Needs deploy",
      value: stats?.pending_deploy ?? 0,
      icon: "layers",
      tone: "slate",
      onClick: () => navigate("/tenants"),
    },
  ];

  return (
    <div className="space-y-5">
      <PageHead
        title="Dashboard"
        sub="Platform overview across all venue deployments"
        action={
          <Btn icon="plus" onClick={() => navigate("/tenants?new=1")}>
            New tenant
          </Btn>
        }
      />

      {/* Stat tiles */}
      {statsLoading ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-xl" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {tiles.map((t) => (
            <StatTile key={t.label} {...t} />
          ))}
        </div>
      )}

      {/* Charts row */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <StatusBreakdown
          stats={
            stats ?? { total: 0, active: 0, provisioning: 0, suspended: 0 }
          }
        />
        <DeployTrendCard />
        <Card className="p-4">
          <h3 className="text-[13px] font-bold text-foreground">
            Quick actions
          </h3>
          <div className="mt-3 space-y-2">
            <button
              onClick={() => navigate("/tenants?new=1")}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left hover:bg-muted"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Icon name="plus" size={16} />
              </span>
              <span className="text-[13px] font-medium text-foreground">
                Create new tenant
              </span>
            </button>
            <button
              onClick={() => navigate("/tenants")}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left hover:bg-muted"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                <Icon name="building" size={16} />
              </span>
              <span className="text-[13px] font-medium text-foreground">
                View all tenants
              </span>
            </button>
          </div>
        </Card>
      </div>

      {/* Trials + Recent */}
      <TrialsEndingCard tenants={tenants} navigate={navigate} />
      {tenantsLoading ? (
        <Skeleton className="h-48 rounded-xl" />
      ) : (
        <RecentTenantsCard tenants={tenants} navigate={navigate} />
      )}
    </div>
  );
}
